import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="App-main">
      <div className="report-page" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h2>Privacy Policy</h2>
        <p className="status-text">Last updated: September 1, 2026 &middot; Version 1.0</p>

        <div className="dossier-section">
          <h3>1. Introduction</h3>
          <p>
            Rentual Corp ("Rentual", "we", "us", "our") is committed to protecting the privacy and personal
            information of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you use the Rentual platform at rentual.co (the "Platform").
          </p>
          <p>
            We are committed to compliance with the Personal Information Protection and Electronic Documents Act
            (PIPEDA) and applicable provincial privacy legislation. By using the Platform, you consent to the
            practices described in this policy.
          </p>
        </div>

        <div className="dossier-section">
          <h3>2. Information We Collect</h3>

          <h4>Account Information</h4>
          <p>When you create an account, we collect your name, email address, phone number, date of birth, and mailing address.</p>

          <h4>Identity Verification Data</h4>
          <p>
            If you use identity verification features, your government-issued identification and selfie are
            processed by Stripe Identity. Rentual does not store biometric data or copies of your identification
            documents. Verification results (status only) are stored by Rentual.
          </p>

          <h4>Application Data</h4>
          <p>
            Tenants may submit employment information, income details, employer references, landlord references,
            and other information as part of rental applications.
          </p>

          <h4>Credit Report Data</h4>
          <p>
            With your consent, credit reports may be obtained from TransUnion or other consumer reporting agencies.
            Credit report data is stored temporarily on the Platform as described in our Data Retention section.
          </p>

          <h4>Documents</h4>
          <p>
            Tenants may upload supporting documents such as pay stubs, employment letters, bank statements, and
            other files. These documents may be processed by AI services for field extraction and verification.
          </p>

          <h4>Usage Data</h4>
          <p>
            We automatically collect information about how you interact with the Platform, including pages visited,
            features used, IP address, browser type, device information, and timestamps.
          </p>

          <h4>Payment Data</h4>
          <p>
            Payment information (credit card numbers, bank account details) is processed directly by Stripe.
            Rentual does not store full payment card numbers or bank account numbers. We store only transaction
            references, amounts, and status information necessary to provide the Services.
          </p>
        </div>

        <div className="dossier-section">
          <h3>3. How We Use Information</h3>
          <p>We use your information to:</p>
          <ul>
            <li>Create and manage your account</li>
            <li>Facilitate rental applications and tenant screening workflows</li>
            <li>Verify your identity when required</li>
            <li>Process AI-assisted document review and risk assessment</li>
            <li>Enable communication between Landlords and Tenants</li>
            <li>Process rent payments and manage financial transactions</li>
            <li>Generate and manage lease documents</li>
            <li>Send transactional notifications (application updates, payment confirmations, etc.)</li>
            <li>Prevent fraud, abuse, and security incidents</li>
            <li>Improve the Platform, develop new features, and analyze usage patterns</li>
            <li>Comply with legal obligations and respond to lawful requests</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>4. AI Processing Disclosure</h3>
          <p>
            Rentual uses third-party artificial intelligence services to assist with document review and
            risk assessment as part of the tenant screening process. This section provides important details
            about how your information may be processed by AI systems.
          </p>
          <ul>
            <li>
              Documents submitted as part of rental applications may be processed using third-party AI services
              (currently Anthropic/Claude) for field extraction, data verification, risk scoring, and fraud detection.
            </li>
            <li>
              AI processing is limited to screening purposes directly related to evaluating a tenancy application.
              Your data is not used for any other purpose.
            </li>
            <li>
              Data sent to AI service providers is processed under data processing agreements that prohibit the
              use of your data for model training or any purpose other than providing the requested analysis.
            </li>
            <li>
              AI-generated outputs, including risk scores, flags, and summaries, are advisory only. All final
              decisions regarding rental applications are made by Landlords, not by AI systems.
            </li>
            <li>
              You have the right to request information about how AI processing was applied to your application.
              Contact us at <a href="mailto:support@rentual.co">support@rentual.co</a> for details.
            </li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>5. Information Sharing</h3>
          <p>We may share your information in the following circumstances:</p>
          <ul>
            <li>
              <strong>With Landlords:</strong> When you submit a rental application, your application data,
              screening results, and uploaded documents are shared with the Landlord for the purpose of
              evaluating your tenancy.
            </li>
            <li>
              <strong>With Service Providers:</strong> We share data with trusted third-party providers who
              help us operate the Platform, including:
              <ul>
                <li>Stripe (payment processing, identity verification)</li>
                <li>TransUnion (credit reports and consumer screening)</li>
                <li>Anthropic (AI-assisted document analysis)</li>
                <li>Cloudinary (document and image hosting)</li>
                <li>Postmark (transactional email delivery)</li>
              </ul>
            </li>
            <li>
              <strong>As Required by Law:</strong> We may disclose information when required by law, regulation,
              court order, or governmental request.
            </li>
            <li>
              <strong>For Safety and Security:</strong> We may disclose information to prevent fraud, protect
              the safety of Users, or enforce our Terms of Service.
            </li>
          </ul>
          <p>
            <strong>We never sell your personal information to third parties.</strong>
          </p>
        </div>

        <div className="dossier-section">
          <h3>6. Data Retention</h3>
          <p>We retain personal information based on the following schedule:</p>
          <table className="data-table" style={{ width: '100%', marginTop: '0.75rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Data Type</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Retention Period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '0.5rem' }}>Abandoned applications</td>
                <td style={{ padding: '0.5rem' }}>60 days from last activity</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem' }}>Declined application documents</td>
                <td style={{ padding: '0.5rem' }}>90 days from decision</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem' }}>Approved application documents</td>
                <td style={{ padding: '0.5rem' }}>1 year from approval</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem' }}>Screening reports</td>
                <td style={{ padding: '0.5rem' }}>60 days from generation</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem' }}>Credit report data</td>
                <td style={{ padding: '0.5rem' }}>90 days from generation</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem' }}>Account data</td>
                <td style={{ padding: '0.5rem' }}>Duration of account + 30 days</td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginTop: '0.75rem' }}>
            You may request earlier deletion of your data where permitted by law.
            Contact <a href="mailto:support@rentual.co">support@rentual.co</a> to submit a deletion request.
          </p>
        </div>

        <div className="dossier-section">
          <h3>7. Data Security</h3>
          <p>
            We implement administrative, technical, and physical safeguards to protect your personal information,
            including:
          </p>
          <ul>
            <li>Encryption in transit using TLS (Transport Layer Security)</li>
            <li>Secure database storage with access controls</li>
            <li>Regular security reviews and monitoring</li>
            <li>Role-based access controls limiting employee access to personal data</li>
            <li>Secure authentication with hashed passwords and token-based sessions</li>
          </ul>
          <p>
            No system is 100% secure. While we take reasonable measures to protect your information, we cannot
            guarantee absolute security.
          </p>
        </div>

        <div className="dossier-section">
          <h3>8. Your Rights Under PIPEDA</h3>
          <p>Under the Personal Information Protection and Electronic Documents Act (PIPEDA), you have the right to:</p>
          <ul>
            <li>
              <strong>Access your personal information:</strong> You may request a copy of the personal information
              we hold about you.
            </li>
            <li>
              <strong>Request correction:</strong> You may request that we correct inaccurate or incomplete
              personal information.
            </li>
            <li>
              <strong>Withdraw consent:</strong> You may withdraw your consent to the collection, use, or
              disclosure of your personal information at any time, subject to legal or contractual restrictions.
              Please note that withdrawing consent may affect your ability to use certain features of the Platform,
              including screening services and AI-assisted document review.
            </li>
            <li>
              <strong>File a complaint:</strong> If you believe your privacy rights have been violated, you may
              file a complaint with the Office of the Privacy Commissioner of Canada at{' '}
              <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">priv.gc.ca</a>.
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:support@rentual.co">support@rentual.co</a>. We will respond to your request within
            30 days as required by PIPEDA.
          </p>
        </div>

        <div className="dossier-section">
          <h3>9. Cookies and Local Storage</h3>
          <ul>
            <li>
              <strong>Essential cookies:</strong> Used for authentication, session management, and security.
              These are necessary for the Platform to function.
            </li>
            <li>
              <strong>Analytics cookies:</strong> With your consent, we may use analytics tools (such as
              Google Analytics) to understand how Users interact with the Platform. You can manage analytics
              preferences through the cookie settings in the site footer.
            </li>
            <li>
              <strong>Local storage:</strong> We use browser local storage to save your preferences (such as
              theme settings) and session tokens.
            </li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>10. Cross-Border Data Transfers</h3>
          <p>
            Your personal information may be processed and stored in servers located outside of Canada, including
            the United States. Our service providers, including Render (hosting), Anthropic (AI processing),
            Stripe (payments and identity verification), and Cloudinary (file storage), may process your data in
            the United States or other jurisdictions.
          </p>
          <p>
            When your data is transferred outside of Canada, it may be subject to the laws of the jurisdiction
            where it is processed. We require our service providers to maintain appropriate safeguards consistent
            with PIPEDA requirements.
          </p>
        </div>

        <div className="dossier-section">
          <h3>11. Children</h3>
          <p>
            The Platform is not intended for use by individuals under the age of 18. We do not knowingly collect
            personal information from children. If we become aware that we have collected personal information
            from a person under 18, we will take steps to delete that information promptly.
          </p>
        </div>

        <div className="dossier-section">
          <h3>12. Changes to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by
            email or through a prominent notice on the Platform. The "Last updated" date at the top of this
            policy indicates when it was most recently revised.
          </p>
          <p>
            Your continued use of the Platform after changes take effect constitutes acceptance of the updated
            policy.
          </p>
        </div>

        <div className="dossier-section">
          <h3>13. Contact</h3>
          <p>
            If you have questions about this Privacy Policy or our privacy practices, please contact our
            Privacy Officer at:
          </p>
          <p>
            <strong>Rentual Corp</strong><br />
            Privacy Officer<br />
            Email: <a href="mailto:support@rentual.co">support@rentual.co</a><br />
            You can also reach us through our <Link to="/contact">contact page</Link>.
          </p>
        </div>
      </div>
    </main>
  );
}

export default Privacy;
